import { useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ExerciseDropdown } from 'components/shared/ExerciseDropdown';
import { CycleDayPreview } from 'hooks/coach/useImportWorkoutCycleAI';

interface Props {
  day: CycleDayPreview;
  dayIdx: number;
  catalogExercises: Array<{ id: number; name: string }>;
  onNameChange: (idx: number, name: string) => void;
  onRemove: (idx: number) => void;
  onResolveExercise: (dayIdx: number, exerciseInternalId: string | number, catalogId: number, name: string) => void;
  onCreateNewExercise: (dayIdx: number, exerciseInternalId: string | number, query: string) => void;
}

export default function CycleDayCard({
  day,
  dayIdx,
  catalogExercises,
  onNameChange,
  onRemove,
  onResolveExercise,
  onCreateNewExercise
}: Props) {
  const [collapsed, setCollapsed] = useState<boolean>(true);

  const totalExercises = day.groups.reduce((acc, g) => acc + g.exercises.length, 0);
  const unresolved = day.groups.reduce((acc, g) => acc + g.exercises.filter((ex) => ex.exercise.id == null).length, 0);

  return (
    <div
      style={{
        background: 'var(--ios-card-bg)',
        border: '1px solid var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-md)',
        boxShadow: 'var(--ios-shadow-sm)',
        overflow: 'hidden',
        marginBottom: '0.5rem'
      }}
    >
      {/* Header */}
      <div
        className="flex align-items-center gap-2"
        style={{
          padding: '0.6rem 0.75rem',
          borderBottom: collapsed ? 'none' : '1px solid var(--ios-divider)',
          background: 'var(--ios-surface-subtle)'
        }}
      >
        <Button
          icon={collapsed ? 'pi pi-chevron-right' : 'pi pi-chevron-down'}
          className="p-button-text p-button-sm p-button-rounded"
          onClick={() => setCollapsed((v) => !v)}
          style={{ width: '1.75rem', height: '1.75rem', flexShrink: 0 }}
        />
        <div style={{ minWidth: '2.5rem', fontWeight: 700, color: 'var(--ios-text-secondary)' }}>
          Día {day.dayNumber}
        </div>
        <InputText
          className="p-inputtext-sm flex-grow-1"
          value={day.instanceName}
          onChange={(e) => onNameChange(dayIdx, e.target.value)}
          maxLength={255}
          style={{ minWidth: 0 }}
        />
        <div
          style={{
            fontSize: '0.75rem',
            color: unresolved > 0 ? '#ef4444' : 'var(--ios-text-secondary)',
            whiteSpace: 'nowrap'
          }}
        >
          {totalExercises} ejercicios
          {unresolved > 0 && (
            <span style={{ marginLeft: '0.35rem', fontWeight: 700 }}>· {unresolved} sin matchear</span>
          )}
        </div>
        <Button
          icon="pi pi-trash"
          tooltip="Quitar este día del ciclo"
          className="p-button-text p-button-sm p-button-rounded"
          onClick={() => onRemove(dayIdx)}
          style={{ color: '#ef4444', width: '1.75rem', height: '1.75rem', flexShrink: 0 }}
        />
      </div>

      {/* Body */}
      {!collapsed && (
        <div style={{ padding: '0.5rem 0.75rem' }}>
          {day.groups.map((group, gIdx) => (
            <div key={group.id} style={{ marginBottom: '0.5rem' }}>
              <div
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--ios-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  marginBottom: '0.25rem'
                }}
              >
                {group.name || `Grupo ${gIdx + 1}`}
              </div>
              {group.exercises.map((ex) => {
                const isUnresolved = ex.exercise.id == null;
                return (
                  <div
                    key={ex.id}
                    className="flex align-items-center gap-2"
                    style={{
                      padding: '0.3rem 0.5rem',
                      borderRadius: 'var(--ios-radius-sm)',
                      background: isUnresolved ? 'rgba(239, 68, 68, 0.08)' : 'var(--ios-surface-subtle)',
                      marginBottom: '0.25rem',
                      fontSize: '0.85rem'
                    }}
                  >
                    {isUnresolved ? (
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '0.72rem',
                            color: '#b91c1c',
                            marginBottom: '0.15rem'
                          }}
                        >
                          Sin matchear: <strong>{ex.exercise.name}</strong>
                        </div>
                        <ExerciseDropdown
                          exercises={catalogExercises}
                          value={ex.exercise.id}
                          onChange={(val) => {
                            if (val) {
                              const match = catalogExercises.find((c) => c.id === val);
                              onResolveExercise(dayIdx, ex.id, val, match?.name ?? ex.exercise.name);
                            }
                          }}
                          onCreateNew={(query) => onCreateNewExercise(dayIdx, ex.id, query)}
                          className="w-full p-inputtext-sm"
                        />
                      </div>
                    ) : (
                      <span className="flex-grow-1" style={{ fontWeight: 500 }}>
                        {ex.exercise.name}
                      </span>
                    )}
                    <span style={{ color: 'var(--ios-text-tertiary)', whiteSpace: 'nowrap' }}>
                      {ex.sets || '—'} × {ex.repetitions || '—'} {ex.weight ? `· ${ex.weight}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
